import React from 'react';
import { Tooltip, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { IconSparkles, IconAlertTriangle } from '@tabler/icons-react';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { TEAL, CORAL } from '../../theme/creamyTokens';

interface AiFilledMarkProps {
  /** False renders nothing at all -- every call site can pass its own condition straight through without wrapping it in a ternary. */
  filled?: boolean;
  /**
   * 'check' for the four fields somebody else pays for if they are wrong --
   * see AI_CHECK_FIELDS. Coral rather than teal, because "look at this" and
   * "this is done" should never be the same colour.
   */
  tone?: 'normal' | 'check';
}

/**
 * The little sparkle that says "this number is a suggestion, not your answer".
 *
 * One of these next to every field the publishing assistant filled in. It
 * exists because of what the host is looking at: a form that is suddenly full
 * of correct-looking numbers they did not type, and no way to tell which ones
 * are theirs. Without the mark the only honest thing to do would be to make
 * them re-read all sixty fields, which is the work this was supposed to save.
 *
 * Deliberately a mark and not a warning. Most of what it points at is right,
 * and a form covered in red is a form nobody reads.
 */
const AiFilledMark: React.FC<AiFilledMarkProps> = ({ filled, tone = 'normal' }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  if (!filled) return null;

  const isCheck = tone === 'check';
  const title = translate(t, isCheck ? TRANSLATIONS.MACHINERY_AI_FILLED_CHECK_HINT : TRANSLATIONS.MACHINERY_AI_FILLED_HINT);

  return (
    <Tooltip title={title}>
      <span
        aria-label={title}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 3,
          marginLeft: 6,
          padding: '1px 6px',
          borderRadius: 999,
          fontSize: 10,
          fontWeight: 600,
          lineHeight: 1.6,
          color: isCheck ? CORAL : TEAL,
          background: isCheck ? token.colorErrorBg : token.colorSuccessBg,
          verticalAlign: 'middle',
        }}
      >
        {isCheck ? <IconAlertTriangle size={11} /> : <IconSparkles size={11} />}
        {translate(t, TRANSLATIONS.MACHINERY_AI_FILLED_TAG)}
      </span>
    </Tooltip>
  );
};

export default AiFilledMark;
